<template>
  <label
    :name="facetId + filter"
    class="group relative flex aspect-square w-64 items-center justify-center rounded-full"
    :class="[
      isDisabled ? 'cursor-not-allowed' : 'cursor-pointer',
      'facet-term-color-' + filter,
    ]"
  >
    <input
      v-model="selected"
      :value="filter"
      :disabled="isDisabled"
      type="checkbox"
      class="peer absolute inset-0 cursor-pointer appearance-none rounded-full focus-visible:outline-2 focus-visible:outline-offset-4"
    />
    <div
      class="pointer-events-none h-32 w-32 scale-75 rounded-full p-4 opacity-0 transition duration-150 ease-swing peer-checked:scale-100 peer-checked:opacity-100"
      :class="isBlack ? 'bg-white text-black' : 'bg-black text-white'"
    >
      <SpriteSymbol name="check" class="h-full w-full" />
    </div>

    <div
      class="caret-bottom pointer-events-none absolute bottom-full left-1/2 mb-24 min-w-full -translate-x-1/2 rounded bg-grey-dark-01 px-10 py-6 text-center text-white opacity-0 group-hover:opacity-100 peer-focus-visible:opacity-100"
    >
      {{ label }}
    </div>

    <div
      v-if="isDisabled"
      class="absolute inset-0 rounded-full bg-white/90 p-10"
      :class="isWhite ? 'text-grey-dark-03' : 'text-black mix-blend-lighten'"
    >
      <SpriteSymbol name="close" class="h-full w-full" />
    </div>
  </label>
</template>

<script lang="ts" setup>
const props = defineProps<{
  facetId: string
  filter: string
  count: number
}>()

const { $texts } = useNuxtApp()

const selected = defineModel<boolean | string[]>()
const isDisabled = computed(() => !props.count)

const isWhite = computed(() => props.filter === 'white')
const isBlack = computed(() => props.filter === 'black')

const label = computed(() => {
  switch (props.filter) {
    case 'black':
      return $texts('productColorBlack', 'Black')
    case 'blue':
      return $texts('productColorBlue', 'Blue')
    case 'brown':
      return $texts('productColorBrown', 'Brown')
    case 'green':
      return $texts('productColorGreen', 'Green')
    case 'grey':
      return $texts('productColorGrey', 'Grey')
    case 'red':
      return $texts('productColorRed', 'Red')
    case 'white':
      return $texts('productColorWhite', 'White')
    case 'multicoloured':
      return $texts('productColorMulticoloured', 'Multicoloured')
    case 'purple':
      return $texts('productColorPurple', 'Purple')
    case 'yellow':
      return $texts('productColorYellow', 'Yellow')
    case 'pink':
      return $texts('productColorPink', 'Pink')
    case 'orange':
      return $texts('productColorOrange', 'Orange')
  }

  return props.filter
})
</script>

<style lang="postcss">
.facet-term-color-white {
  @apply border border-grey-light-01;
  background: linear-gradient(126.67deg, #f9f9f9 15.25%, #ffffff 84.38%);
}

.facet-term-color-grey {
  background: linear-gradient(126.67deg, #b4b3b3 15.25%, #d4d0d0 84.38%);
}

.facet-term-color-black {
  background: linear-gradient(126.67deg, #535353 15.25%, #000000 84.38%);
}

.facet-term-color-blue {
  background: linear-gradient(126.67deg, #264db0 15.25%, #1884e7 84.38%);
}

.facet-term-color-green {
  background: linear-gradient(126.67deg, #3c8b2f 15.25%, #3dc15a 84.38%);
}

.facet-term-color-red {
  background: linear-gradient(126.67deg, #f30b0b 15.25%, #e02727 84.38%);
}

.facet-term-color-orange {
  background: linear-gradient(126.67deg, #ef7f18 15.25%, #e47d1e 84.38%);
}

.facet-term-color-yellow {
  background: linear-gradient(126.67deg, #e7e14e 15.25%, #f1ec82 84.38%);
}

.facet-term-color-brown {
  background: linear-gradient(126.67deg, #c57c0f 15.25%, #d38715 84.38%);
}

.facet-term-color-purple {
  background: linear-gradient(126.67deg, #9a27c2 15.25%, #b531f4 84.38%);
}

.facet-term-color-pink {
  background: linear-gradient(126.67deg, #ff769f 15.25%, #eb4f90 84.38%);
}

.facet-term-color-multicoloured {
  background: conic-gradient(
    from 158.34deg at 50% 49.57%,
    #4a28d2 -34.24deg,
    #ff9696 46.77deg,
    #ca0ef9 138.75deg,
    rgba(100, 217, 175, 0.39) 221.98deg,
    #4a28d2 325.76deg,
    #ff9696 406.77deg
  );
}

.caret-bottom {
  &:after {
    @apply absolute left-1/2 top-full -translate-x-1/2;
    content: '';
    width: 0px;
    height: 0px;
    border-style: solid;
    border-width: 12px 10.5px 0 10.5px;
    @apply border-transparent;
    @apply border-t-grey-dark-01;
  }
}
</style>
